// Header.jsx — BerBul design system · matches TradeDashboard tokens
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { getUser, resetUserSession, getToken } from "../../service/AuthService";

// ─── Design tokens (mirrors TradeDashboard) ───────────────────────────────────
const C = {
  bg:     "#08101e",
  bg1:    "#0c1525",
  bg2:    "#101b2e",
  border: "#1e2d42",
  text:   "#e8edf5",
  body:   "#b8c8dc",
  meta:   "#6a7d95",
  cyan:   "#00c8ff",
  green:  "#00e896",
};

const FONT = "'DM Mono', monospace";

function Header() {
  const { pathname } = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [name, setName]             = useState("");

  const menuItems = [
    { label: "HOME",         path: "/Home"     },
    { label: "OUR APPROACH", path: "/approach" },   // ← NEW
    { label: "DASHBOARD",    path: "/trading"  },
    { label: "LIVE",         path: "/Live-New" },
    { label: "ADMIN",        path: "/Admin"    },
  ];

  function handleLogout() {
    setIsLoggedIn(false);
    setName("");
    resetUserSession();
    window.location.href = "/Home";
  }

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setIsLoggedIn(false);
      setName("");
      return;
    }
    setIsLoggedIn(true);
    const user = getUser();
    setName(user ? (user.username || "") : "");
  }, [pathname]);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500&display=swap');
        .berbul-header-nav-item { text-decoration: none; transition: background .12s, color .12s; }
        .berbul-header-nav-item:hover { background: ${C.bg2} !important; }

        /* "Our Approach" gets a subtle green tint when inactive */
        .berbul-header-nav-approach { color: ${C.green} !important; }
        .berbul-header-nav-approach:hover { background: ${C.green}0d !important; }

        .berbul-header-btn { cursor: pointer; transition: background .12s, border-color .12s; }
        .berbul-header-btn:hover { background: ${C.bg2} !important; border-color: ${C.border} !important; }
        .berbul-header-btn-primary:hover { border-color: ${C.cyan} !important; color: ${C.cyan} !important; }
      `}</style>

      <header style={{
        background:   C.bg1,
        borderBottom: `1px solid ${C.border}`,
        position:     "sticky",
        top:          0,
        zIndex:       1000,
      }}>
        <div style={{
          maxWidth:       1200,
          margin:         "0 auto",
          padding:        "0 32px",
          height:         52,
          display:        "flex",
          alignItems:     "center",
          justifyContent: "space-between",
          gap:            16,
        }}>

          {/* ── Logo ─────────────────────────────────────────────────────── */}
          <Link to="/" aria-label="BerBul.ai Home" style={{ display: "flex", alignItems: "center", textDecoration: "none", flexShrink: 0, gap: 11 }}>

            {/* ── Orb Icon ── */}
            <div style={{
              position:      "relative",
              width:         46,
              height:        46,
              borderRadius:  "50%",
              overflow:      "hidden",
              background:    "radial-gradient(circle at 32% 32%, rgba(255,255,255,0.16) 0%, rgba(14,24,42,0.92) 55%, rgba(8,16,30,0.98) 100%)",
              border:        "1px solid rgba(255,255,255,0.20)",
              boxShadow:     [
                "0 0 0 1px rgba(0,200,255,0.15)",
                "0 0 20px rgba(0,200,255,0.22)",
                "0 0 40px rgba(0,232,150,0.12)",
                "inset 0 -12px 28px rgba(0,0,0,0.55)",
                "inset 0 2px 0 rgba(255,255,255,0.18)",
              ].join(", "),
              display:       "flex",
              flexDirection: "column",
              alignItems:    "center",
              justifyContent:"center",
              gap:           2.5,
            }}>
              {[
                { color: "#4488FF", glow: "rgba(68,136,255,0.60)"  },
                { color: "#00C8FF", glow: "rgba(0,200,255,0.60)"   },
                { color: "#00E896", glow: "rgba(0,232,150,0.90)", bold: true },
                { color: "#9966FF", glow: "rgba(153,102,255,0.60)" },
                { color: "#4488FF", glow: "rgba(68,136,255,0.60)"  },
              ].map((bar, i) => (
                <div key={i} style={{
                  width:        bar.bold ? "47%" : "44%",
                  height:       bar.bold ? 3 : 2.5,
                  borderRadius: 999,
                  background:   bar.color,
                  opacity:      bar.bold ? 1 : 0.80,
                  boxShadow:    `0 0 5px ${bar.glow}, 0 0 10px ${bar.glow}`,
                  flexShrink:   0,
                }} />
              ))}

              {/* top-left specular glint */}
              <div style={{
                position:      "absolute",
                top:           5,
                left:          8,
                width:         16,
                height:        9,
                borderRadius:  "50%",
                background:    "rgba(255,255,255,0.18)",
                filter:        "blur(4px)",
                pointerEvents: "none",
              }} />

              {/* bottom-right depth shadow */}
              <div style={{
                position:      "absolute",
                bottom:        4,
                right:         5,
                width:         18,
                height:        12,
                borderRadius:  "50%",
                background:    "rgba(0,0,0,0.30)",
                filter:        "blur(5px)",
                pointerEvents: "none",
              }} />
            </div>

            {/* ── Wordmark ── */}
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 16, fontWeight: 500, letterSpacing: "0.10em", lineHeight: 1, userSelect: "none" }}>
              <span style={{ color: "#e8edf5" }}>BerBul</span>
              <span style={{ color: "#00c8ff", letterSpacing: "0.05em" }}>.ai</span>
            </span>

          </Link>

          {/* ── Nav ──────────────────────────────────────────────────────── */}
          <nav aria-label="Primary" style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {menuItems.map(item => {
              const isActive   = pathname === item.path;
              const isApproach = item.path === "/approach";

              return (
                <Link
                  key={item.label}
                  to={item.path}
                  className={`berbul-header-nav-item${isApproach && !isActive ? " berbul-header-nav-approach" : ""}`}
                  aria-current={isActive ? "page" : undefined}
                  style={{
                    fontFamily:    FONT,
                    fontSize:      10,
                    fontWeight:    500,
                    letterSpacing: "1px",
                    padding:       "6px 12px",
                    borderRadius:  4,
                    color:      isActive ? C.cyan : isApproach ? C.green : C.body,
                    background: isActive ? C.cyan + "12" : "transparent",
                    border:     isActive ? `1px solid ${C.cyan}30` : "1px solid transparent",
                    position:   "relative",
                  }}
                >
                  {item.label}
                  {isActive && (
                    <span style={{
                      position:     "absolute",
                      bottom:       -10,
                      left:         "50%",
                      transform:    "translateX(-50%)",
                      width:        3,
                      height:       3,
                      borderRadius: "50%",
                      background:   C.cyan,
                    }} />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* ── Actions ──────────────────────────────────────────────────── */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {isLoggedIn ? (
              <>
                {name && (
                  <div style={{
                    display:      "flex",
                    alignItems:   "center",
                    gap:          8,
                    padding:      "4px 10px 4px 5px",
                    borderRadius: 6,
                    border:       `1px solid ${C.border}`,
                    background:   C.bg,
                  }}>
                    <div style={{
                      width:         24,
                      height:        24,
                      borderRadius:  "50%",
                      background:    C.cyan + "20",
                      border:        `1px solid ${C.cyan}40`,
                      display:       "grid",
                      placeItems:    "center",
                      fontFamily:    FONT,
                      fontSize:      10,
                      fontWeight:    500,
                      color:         C.cyan,
                      letterSpacing: ".5px",
                      flexShrink:    0,
                    }}>
                      {name.charAt(0).toUpperCase()}
                    </div>
                    <span style={{
                      fontFamily:    FONT,
                      fontSize:      10,
                      color:         C.body,
                      maxWidth:      120,
                      overflow:      "hidden",
                      textOverflow:  "ellipsis",
                      whiteSpace:    "nowrap",
                      letterSpacing: ".3px",
                    }}>
                      {name}
                    </span>
                  </div>
                )}
                <button
                  className="berbul-header-btn"
                  onClick={handleLogout}
                  style={{
                    fontFamily:    FONT,
                    fontSize:      9,
                    fontWeight:    500,
                    letterSpacing: ".8px",
                    color:         C.meta,
                    background:    "transparent",
                    border:        `1px solid ${C.border}`,
                    borderRadius:  4,
                    padding:       "5px 12px",
                  }}
                >
                  LOGOUT
                </button>
              </>
            ) : (
              <Link to="/Login" style={{ textDecoration: "none" }}>
                <button
                  className="berbul-header-btn berbul-header-btn-primary"
                  style={{
                    fontFamily:    FONT,
                    fontSize:      9,
                    fontWeight:    500,
                    letterSpacing: ".8px",
                    color:         C.body,
                    background:    "transparent",
                    border:        `1px solid ${C.border}`,
                    borderRadius:  4,
                    padding:       "5px 14px",
                  }}
                >
                  LOGIN
                </button>
              </Link>
            )}
          </div>

        </div>
      </header>
    </>
  );
}

export default Header;